import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Flag, Sparkles, Trash2, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-primary/15 text-primary",
  completed: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  archived: "bg-secondary text-muted-foreground",
};

export default function GoalsList() {
  const t = useT();
  usePageMeta(t("goals_section_title"));
  const navigate = useNavigate();
  const [goals, setGoals] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/goals"); setGoals(Array.isArray(data) ? data : []); }
    catch { setGoals([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const statusLabel = (s) => t(`goals_status_${s}`) || t("goals_status_active");

  const remove = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(t("goals_actions_body") || "Delete this goal?")) return;
    try { await api.delete(`/goals/${id}`); setGoals((g) => g.filter((x) => x.id !== id)); toast.success(t("jr.deleted") || "Deleted"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="goals-list-screen">
      <div className="flex items-start justify-between gap-3">
        <PageHeader eyebrow={t("nav.group.grow")} title={t("goals_section_title")} subtitle={t("goals_section_subtitle")} />
        <Button onClick={() => navigate("/app/goals/new")} data-testid="goals-add-header" className="shrink-0 gap-1.5 rounded-full">
          <Plus className="h-4 w-4" /> {t("goals_add")}
        </Button>
      </div>

      {goals === null ? (
        <Spinner label={t("common.loading")} />
      ) : goals.length === 0 ? (
        <EmptyState icon={Flag} title={t("goals_empty_title")} description={t("goals_empty_body")}
          action={<Button onClick={() => navigate("/app/goals/new")} data-testid="goals-empty-add" className="gap-1.5 rounded-full"><Plus className="h-4 w-4" /> {t("goals_add_first")}</Button>} />
      ) : (
        <div className="space-y-2.5">
          {goals.map((g) => {
            const last = (g.progress_log || []).slice(-1)[0];
            return (
              <Link key={g.id} to={`/app/goals/${g.id}`} data-testid={`goal-list-${g.id}`}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/30">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="font-display text-base text-foreground">{g.title}</span>
                    {g.weva_analysis_enabled ? <span className="inline-flex items-center gap-1 rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary"><Sparkles className="h-2.5 w-2.5" /> WEVA</span> : null}
                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[g.status] || STATUS_STYLE.active}`}>{statusLabel(g.status)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {last ? t("goals_last_scale", { n: last.scale }) : (g.answers?.q6_timeframe || t("goals_no_progress_yet"))}
                  </p>
                </div>
                <button onClick={(e) => remove(e, g.id)} data-testid={`goal-delete-${g.id}`} className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
